import type { ChartOptions, ChartType, ScriptableContext } from 'chart.js'

const colors = [
  '#A268FF',
  '#FF7777',
  '#FFCC68',
  '#00C2EE',
  '#679BFF',
  '#00EE98',
  '#BAC3CF',
  '#505D6F',
]

const oColors = [
  'rgba(162, 104, 255, 0.1)',
  'rgba(255, 119, 119, 0.1)',
  'rgba(255, 204, 104, 0.1)',
  'rgba(0, 194, 238, 0.1)',
  'rgba(103, 155, 255, 0.1)',
  'rgba(0, 238, 152, 0.1)',
  'rgba(186, 195, 207, 0.1)',
  'rgba(80, 93, 111, 0.1)',
]

const oColors2 = [
  'rgba(162, 104, 255, 0.2)',
  'rgba(255, 119, 119, 0.2)',
  'rgba(255, 204, 104, 0.2)',
  'rgba(0, 194, 238, 0.2)',
  'rgba(103, 155, 255, 0.2)',
  'rgba(0, 238, 152, 0.2)',
  'rgba(186, 195, 207, 0.2)',
  'rgba(80, 93, 111, 0.2)',
]

const oColors7 = [
  'rgba(162, 104, 255, 0.7)',
  'rgba(255, 119, 119, 0.7)',
  'rgba(255, 204, 104, 0.7)',
  'rgba(0, 194, 238, 0.7)',
  'rgba(103, 155, 255, 0.7)',
  'rgba(0, 238, 152, 0.7)',
  'rgba(186, 195, 207, 0.7)',
  'rgba(80, 93, 111, 0.7)',
]

const gradients = [
  [
    { offset: 0, color: '#FFC371' },
    { offset: 1, color: '#FF5F6D' },
  ],
  [
    { offset: 0.7858, color: '#FF8A1E' },
    { offset: 0.1544, color: '#FBDA61' },
  ],
  [
    { offset: 0.8351, color: '#68C121' },
    { offset: 0.1649, color: '#FFD705' },
  ],
  [
    { offset: 0.1649, color: '#A1FF8B' },
    { offset: 0.8351, color: '#3F93FF' },
  ],
  [
    { offset: 0.141, color: '#10D7E2' },
    { offset: 0.859, color: '#9358F7' },
  ],
  [
    { offset: 0.1649, color: '#7570FF' },
    { offset: 0.8351, color: '#CF36AD' },
  ],
  [
    { offset: 0.1649, color: '#FF56DA' },
    { offset: 0.8351, color: '#FF1B1B' },
  ],
  [
    { offset: 0, color: '#FF5959' },
    { offset: 1, color: '#FF7A00' },
  ],
]

export const flatColors = (ctx: ScriptableContext<ChartType>) => {
  return colors[ctx.datasetIndex % colors.length]
}

export const opaqueColors = (ctx: ScriptableContext<ChartType>) => {
  return oColors[ctx.datasetIndex % colors.length]
}

export const opaqueColors2 = (ctx: ScriptableContext<ChartType>) => {
  return oColors2[ctx.datasetIndex % colors.length]
}

export const opaqueColors7 = (ctx: ScriptableContext<ChartType>) => {
  return oColors7[ctx.datasetIndex % colors.length]
}

export const getFlatColor = (idx: number) => colors[idx % colors.length]

export const getGradientColor = (
  context: { chart: { ctx: any; chartArea: any } },
  idx: number
) => {
  let gradient: { addColorStop: (arg0: number, arg1: string) => void } | null =
    null
  const { chart } = context
  const { ctx, chartArea } = chart

  if (!chartArea) {
    // This case happens on initial chart load
    return
  }

  if (!gradient) {
    gradient = ctx.createLinearGradient(0, 0, 0, chartArea.height)
    gradients[idx % gradients.length].forEach((stop) => {
      gradient!.addColorStop(stop.offset, stop.color)
    })
  }

  return gradient
}

export const getOpaqueGradientColor = (
  context: ScriptableContext<ChartType>,
  idx: number,
  endColor: string | undefined = undefined
) => {
  let gradient
  const { chart } = context
  const { ctx, chartArea } = chart

  if (!chartArea) {
    // This case happens on initial chart load
    return
  }

  if (!gradient) {
    gradient = ctx.createLinearGradient(0, 0, 0, chartArea.height)
    gradient.addColorStop(0, colors[idx % colors.length])
    gradient.addColorStop(1, endColor || oColors[idx % colors.length])
  }

  return gradient
}

function getRadialGradientColor(
  context: ScriptableContext<ChartType>,
  idx = 0
) {
  const chartArea = context.chart.chartArea
  let width, height
  const cache = new Map()

  if (!chartArea) {
    // This case happens on initial chart load
    return
  }

  const chartWidth = chartArea.right - chartArea.left
  const chartHeight = chartArea.bottom - chartArea.top
  if (width !== chartWidth || height !== chartHeight) {
    cache.clear()
  }
  let gradient = cache.get(idx)
  if (!gradient) {
    // Create the gradient because this is either the first render
    // or the size of the chart has changed
    width = chartWidth
    height = chartHeight
    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2
    const r = Math.min(
      (chartArea.right - chartArea.left) / 2,
      (chartArea.bottom - chartArea.top) / 2
    )
    const ctx = context.chart.ctx
    gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      r
    )
    gradients[idx % gradients.length].forEach((stop) => {
      gradient.addColorStop(stop.offset, stop.color)
    })

    cache.set(idx, gradient)
  }

  return gradient
}

function getOpaqueRadialGradientColor(
  context: ScriptableContext<ChartType>,
  idx = 0,
  endColor: string | undefined = undefined
) {
  const chartArea = context.chart.chartArea
  let width, height
  const cache = new Map()

  if (!chartArea) {
    // This case happens on initial chart load
    return
  }

  const chartWidth = chartArea.right - chartArea.left
  const chartHeight = chartArea.bottom - chartArea.top
  if (width !== chartWidth || height !== chartHeight) {
    cache.clear()
  }
  let gradient = cache.get(idx)
  if (!gradient) {
    // Create the gradient because this is either the first render
    // or the size of the chart has changed
    width = chartWidth
    height = chartHeight
    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2
    const r = Math.min(
      (chartArea.right - chartArea.left) / 2,
      (chartArea.bottom - chartArea.top) / 2
    )
    const ctx = context.chart.ctx
    gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      r
    )

    gradient.addColorStop(0, endColor || oColors[idx % colors.length])
    gradient.addColorStop(1, oColors2[idx % colors.length])

    cache.set(idx, gradient)
  }

  return gradient
}

export const gradientColors = (ctx: ScriptableContext<ChartType>) => {
  return getGradientColor(ctx, ctx.datasetIndex)
}

export const radialGradientColors = (ctx: ScriptableContext<ChartType>) => {
  return getRadialGradientColor(ctx, ctx.dataIndex)
}

export const opaqueGradientColors = (ctx: ScriptableContext<ChartType>) => {
  return getOpaqueGradientColor(ctx, ctx.datasetIndex)
}

export const opaqueDarkGradientColors = (ctx: ScriptableContext<ChartType>) => {
  return getOpaqueGradientColor(ctx, ctx.datasetIndex, 'rgba(0,0,0,0)')
}

export const opaqueLightGradientColors = (
  ctx: ScriptableContext<ChartType>
) => {
  return getOpaqueGradientColor(ctx, ctx.datasetIndex, 'rgba(255,255,255,0)')
}

export const opaqueRadialGradientColors = (
  ctx: ScriptableContext<ChartType>
) => {
  return getOpaqueRadialGradientColor(ctx, ctx.datasetIndex)
}

export const opaqueDarkRadialGradientColors = (
  ctx: ScriptableContext<ChartType>
) => {
  return getOpaqueRadialGradientColor(ctx, ctx.datasetIndex, 'rgba(0,0,0,0)')
}

export const opaqueLightRadialGradientColors = (
  ctx: ScriptableContext<ChartType>
) => {
  return getOpaqueRadialGradientColor(
    ctx,
    ctx.datasetIndex,
    'rgba(255,255,255,0)'
  )
}

export const getChartDefaultOptions = (
  type:
    | 'bar'
    | 'bubble'
    | 'donut'
    | 'line'
    | 'pie'
    | 'polar'
    | 'radar'
    | 'scatter'
    | 'custom'
    | ''
): ChartOptions => {
  return {
    responsive: true,
    maintainAspectRatio: true,
    color: '#333333',
    font: {
      family: "'Inter', sans-serif",
      weight: '600',
      size: 12,
      lineHeight: 1.33333333,
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          display: ['bar', 'line', 'bubble', 'scatter'].includes(type)
            ? true
            : false,
          padding: 8,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          display: false,
          dash: [2, 2],
          color: '#eaeaea',
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          display: ['bar', 'line', 'bubble', 'scatter'].includes(type)
            ? true
            : false,
          padding: 8,
        },
        grid: {
          display: ['bar', 'line', 'bubble', 'scatter'].includes(type)
            ? true
            : false,
          drawTicks: false,
        },
        border: {
          display: false,
          dash: [2, 2],
          color: '#eaeaea',
        },
      },
    },
    plugins: {
      legend: {
        display: type === 'polar' ? false : true,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          padding: 24,
        },
      },
    },
    elements: {
      bar:
        type === 'bar'
          ? {
              borderSkipped: false,
              borderRadius: 4,
              backgroundColor: flatColors,
            }
          : {},
      line:
        type === 'line'
          ? {
              borderColor: flatColors,
              backgroundColor: opaqueLightGradientColors,
              borderWidth: 1,
              tension: 0.33,
              fill: 'start',
            }
          : type === 'radar'
          ? {
              tension: 0,
              backgroundColor: opaqueColors2,
              borderColor: flatColors,
              borderWidth: 1,
            }
          : {},
      point: ['line', 'radar'].includes(type)
        ? {
            borderColor: 'rgba(255,255,255,0.7)',
            radius: 4,
            backgroundColor: flatColors,
          }
        : ['bubble', 'scatter'].includes(type)
        ? {
            hoverBorderColor: 'rgba(255,255,255,0.7)',
            backgroundColor: flatColors,
          }
        : {},
      arc: ['donut', 'pie'].includes(type)
        ? {
            borderWidth: 2,
            borderRadius: 6,
            borderColor: '#ffffff',
            hoverBorderColor: '#ffffff',
            backgroundColor: colors,
            hoverOffset: 4,
          }
        : ['polar', 'radar'].includes(type)
        ? {
            borderWidth: 1,
            borderColor: opaqueColors7,
            backgroundColor: opaqueRadialGradientColors,
            hoverBackgroundColor: opaqueColors7,
          }
        : {},
    },
  }
}

export const CHART_COLORS = colors
export const CHART_GRADIENTS = gradients
export const CHART_COLOR_OPAQUE_1 = oColors
export const CHART_COLOR_OPAQUE_2 = oColors2
export const CHART_COLOR_OPAQUE_7 = oColors7
